import api from './api';
import {
  BankAccount, BankAccountRequest, BankTransaction, UpdateBankTransactionRequest,
  TransactionCategory, TransactionCategoryRequest, BankImportResult, BankingSummary,
  InvoiceMatchCandidate, RevertImportResult,
} from '../types';

export interface TransactionFilter {
  accountId?: number;
  from?: string;
  to?: string;
  categoryId?: number;
  uncategorized?: boolean;
  unmatched?: boolean;
  missingVat?: boolean;
  missingReceipt?: boolean;
  search?: string;
  limit?: number;
}

export const bankingService = {
  // Konten
  getAccounts: async (includeInactive = false): Promise<BankAccount[]> => {
    const response = await api.get('/banking/accounts', { params: { includeInactive } });
    return response.data;
  },

  createAccount: async (data: BankAccountRequest): Promise<BankAccount> => {
    const response = await api.post('/banking/accounts', data);
    return response.data;
  },

  updateAccount: async (id: number, data: BankAccountRequest): Promise<BankAccount> => {
    const response = await api.put(`/banking/accounts/${id}`, data);
    return response.data;
  },

  deleteAccount: async (id: number): Promise<void> => {
    await api.delete(`/banking/accounts/${id}`);
  },

  /** CSV-Kontoauszug hochladen; doppelte Buchungen überspringt der Import selbst. */
  importStatement: async (accountId: number, file: File): Promise<BankImportResult> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/banking/accounts/${accountId}/import`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getImports: async (accountId?: number, limit = 25): Promise<BankImportResult[]> => {
    const response = await api.get('/banking/imports', { params: { accountId, limit } });
    return response.data;
  },

  /** Nimmt einen Import samt seiner Umsätze zurück. Bezahlte Rechnungen bleiben bezahlt. */
  revertImport: async (importId: number): Promise<RevertImportResult> => {
    const response = await api.delete(`/banking/imports/${importId}`);
    return response.data;
  },

  // Umsätze
  getTransactions: async (filter: TransactionFilter = {}): Promise<BankTransaction[]> => {
    const response = await api.get('/banking/transactions', { params: filter });
    return response.data;
  },

  getTransaction: async (id: number): Promise<BankTransaction> => {
    const response = await api.get(`/banking/transactions/${id}`);
    return response.data;
  },

  updateTransaction: async (id: number, data: UpdateBankTransactionRequest): Promise<BankTransaction> => {
    const response = await api.put(`/banking/transactions/${id}`, data);
    return response.data;
  },

  getInvoiceCandidates: async (): Promise<InvoiceMatchCandidate[]> => {
    const response = await api.get('/banking/invoice-candidates');
    return response.data;
  },

  // Kategorien
  getCategories: async (includeInactive = false): Promise<TransactionCategory[]> => {
    const response = await api.get('/banking/categories', { params: { includeInactive } });
    return response.data;
  },

  createCategory: async (data: TransactionCategoryRequest): Promise<TransactionCategory> => {
    const response = await api.post('/banking/categories', data);
    return response.data;
  },

  updateCategory: async (id: number, data: TransactionCategoryRequest): Promise<TransactionCategory> => {
    const response = await api.put(`/banking/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/banking/categories/${id}`);
  },

  // Auswertung
  getSummary: async (year?: number): Promise<BankingSummary> => {
    const response = await api.get('/banking/summary', { params: year ? { year } : undefined });
    return response.data;
  },
};

export default bankingService;
